// src/components/CustomerReviews.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import "./CustomerReviews.css";
import useIsMobile from "../hooks/useIsMobile";
import { adjustFontSize } from "../utils/typography";

function safeNum(val) {
  const n = Number(val);
  return Number.isFinite(n) ? n : 0;
}

export default function CustomerReviews({ config, title, subtitle, fontStyles }) {
  // ✅ ALWAYS call hooks (no early return before hooks)
  const isMobile = useIsMobile();
  const items = useMemo(() => {
    const reviews = Array.isArray(config?.reviews) ? config.reviews : [];
    return reviews
      .map((r) => {
        const name = typeof r?.name === "string" ? r.name.trim() : "";
        const description =
          typeof r?.description === "string" ? r.description.trim() : "";
        const ratingRaw = r?.rating;
        const designation =
          typeof r?.designation === "string" ? r.designation.trim() : "";
        return { name, description, rating: Math.round(safeNum(ratingRaw)), designation };
      })
      .filter((x) => x.name && x.description);
  }, [config]);

  const image = typeof config?.image === "string" ? config.image.trim() : "";
  const overlayOpacity =
    typeof config?.overlayOpacity === "number" ? config.overlayOpacity : 0.3;
  const backgroundColor =
    typeof config?.backgroundColor === "string" ? config.backgroundColor : "";

  const sectionStyles = config?.sectionStyles || {};
  const sizeOverrides = sectionStyles.fontSizes || {};

  // ✅ Constant heading/subheading
  const headingText = (typeof title === "string" && title.trim()) || "TESTIMONIALS";
  const subText =
    (typeof subtitle === "string" && subtitle.trim()) ||
    "Our clients’ experiences mean everything to us. Here’s what some of them say about their journey with Blackstone Interior Design Company.";

  const sectionTitleFont = fontStyles?.sectionTitle || fontStyles?.heading || {};
  const sectionDescFont = fontStyles?.sectionDescription || fontStyles?.subHeading || {};

  const titleStyle = {
    fontFamily: sectionTitleFont?.fontFamily || "inherit",
    fontSize: adjustFontSize(
      sizeOverrides.sectionTitle || sectionTitleFont?.fontSize || undefined,
      isMobile
    ),
    fontWeight: sectionTitleFont?.fontWeight || 800,
    lineHeight: sectionTitleFont?.lineHeight || "1.2",
    color: sectionStyles?.headingColor || sectionTitleFont?.color || "#111111",
  };

  const subStyle = {
    fontFamily: sectionDescFont?.fontFamily || "inherit",
    fontSize: adjustFontSize(
      sizeOverrides.sectionDescription || sectionDescFont?.fontSize || undefined,
      isMobile
    ),
    fontWeight: sectionDescFont?.fontWeight || 400,
    lineHeight: sectionDescFont?.lineHeight || "1.6",
    color: sectionStyles?.subHeadingColor || sectionDescFont?.color || "#333333",
  };

  const reviewStyle = {
    fontFamily: fontStyles?.content?.fontFamily || "inherit",
    fontSize: adjustFontSize(
      sizeOverrides.content || fontStyles?.content?.fontSize || undefined,
      isMobile
    ),
    fontWeight: fontStyles?.content?.fontWeight || 400,
    lineHeight: fontStyles?.content?.lineHeight || "1.8",
    color: sectionStyles?.contentColor || fontStyles?.content?.color || "#555555",
  };

  const nameStyle = {
    fontFamily: fontStyles?.subHeading?.fontFamily || "inherit",
    fontSize: adjustFontSize(
      sizeOverrides.subHeading || fontStyles?.subHeading?.fontSize || undefined,
      isMobile
    ),
    fontWeight: fontStyles?.subHeading?.fontWeight || 700,
    lineHeight: fontStyles?.subHeading?.lineHeight || "1.2",
    color:
      sectionStyles?.reviewerNameColor ||
      sectionStyles?.headingColor ||
      fontStyles?.subHeading?.color ||
      "#111111",
  };

  const roleStyle = {
    fontFamily: fontStyles?.content?.fontFamily || "inherit",
    fontSize: adjustFontSize(
      sizeOverrides.content || fontStyles?.content?.fontSize || undefined,
      isMobile
    ),
    fontWeight: fontStyles?.content?.fontWeight || 400,
    lineHeight: fontStyles?.content?.lineHeight || "1.4",
    color:
      sectionStyles?.reviewerDesignationColor ||
      sectionStyles?.contentColor ||
      fontStyles?.content?.color ||
      "#666666",
  };

  const starStyle = {
    color: sectionStyles?.ratingColor || "#FFD700",
  };

  const canRender = items.length > 0 && !!image;

  const trackRef = useRef(null);
  const timerRef = useRef(null);
  const isDraggingRef = useRef(false);
  const startXRef = useRef(0);
  const startScrollRef = useRef(0);

  const [activeIndex, setActiveIndex] = useState(0);

  // Keep activeIndex safe if items change
  useEffect(() => {
    setActiveIndex((prev) => {
      if (!items.length) return 0;
      return Math.max(0, Math.min(prev, items.length - 1));
    });
  }, [items.length]);

  // Auto-slide every 2s (mobile + desktop both) — pauses while dragging
  useEffect(() => {
    if (!canRender) return;

    if (timerRef.current) window.clearInterval(timerRef.current);

    timerRef.current = window.setInterval(() => {
      if (isDraggingRef.current) return;
      const next = (activeIndex + 1) % items.length;
      setActiveIndex(next);

      const el = trackRef.current;
      if (el) {
        el.scrollTo({ left: next * el.clientWidth, behavior: "smooth" });
      }
    }, 2000);

    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
    };
  }, [canRender, activeIndex, items.length]);

  // Snap to correct slide when resize
  useEffect(() => {
    const onResize = () => {
      const el = trackRef.current;
      if (!el) return;
      el.scrollTo({ left: activeIndex * el.clientWidth, behavior: "auto" });
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [activeIndex]);

  // Pointer drag-to-swipe
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;

    const onPointerDown = (e) => {
      isDraggingRef.current = true;
      startXRef.current = e.clientX;
      startScrollRef.current = el.scrollLeft;
      el.setPointerCapture?.(e.pointerId);
    };

    const onPointerMove = (e) => {
      if (!isDraggingRef.current) return;
      const dx = e.clientX - startXRef.current;
      el.scrollLeft = startScrollRef.current - dx;
    };

    const endDrag = () => {
      if (!isDraggingRef.current) return;
      isDraggingRef.current = false;

      // snap to nearest card
      const idx = Math.round(el.scrollLeft / el.clientWidth);
      const next = Math.max(0, Math.min(idx, items.length - 1));
      setActiveIndex(next);
      el.scrollTo({ left: next * el.clientWidth, behavior: "smooth" });
    };

    el.addEventListener("pointerdown", onPointerDown, { passive: true });
    el.addEventListener("pointermove", onPointerMove, { passive: true });
    el.addEventListener("pointerup", endDrag, { passive: true });
    el.addEventListener("pointercancel", endDrag, { passive: true });
    el.addEventListener("mouseleave", endDrag, { passive: true });

    // keep activeIndex in sync if user scrolls by touch
    const onScroll = () => {
      if (isDraggingRef.current) return;
      const idx = Math.round(el.scrollLeft / el.clientWidth);
      if (idx !== activeIndex) setActiveIndex(idx);
    };
    el.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      el.removeEventListener("pointerdown", onPointerDown);
      el.removeEventListener("pointermove", onPointerMove);
      el.removeEventListener("pointerup", endDrag);
      el.removeEventListener("pointercancel", endDrag);
      el.removeEventListener("mouseleave", endDrag);
      el.removeEventListener("scroll", onScroll);
    };
  }, [activeIndex, items.length]);

  if (!canRender) return null;

  return (
    <section
      className="cr2-wrap"
      style={{ background: backgroundColor || "transparent" }}
    >
      <div className="cr2-card">
        {/* LEFT IMAGE (constant) */}
        <div className="cr2-media">
          <img className="cr2-img" src={image} alt="Testimonials" />
          <div
            className="cr2-mediaOverlay"
            style={{ opacity: overlayOpacity }}
          />
        </div>

        {/* RIGHT SIDE (title/subtitle constant, only reviews slide) */}
        <div className="cr2-right">
          <div className="cr2-head">
            <h2 className="cr2-title" style={titleStyle}>{headingText}</h2>
            <p className="cr2-sub" style={subStyle}>{subText}</p>
          </div>

          {/* SLIDER AREA (only this changes) */}
          <div className="cr2-slider" ref={trackRef} aria-label="Testimonials slider">
            {items.map((it, i) => (
              <article key={`${it.name}-${i}`} className="cr2-slide">
                <p className="cr2-reviewText" title={it.description} style={reviewStyle}>
                  {it.description}
                </p>

                <div className="cr2-bottomRow">
                  <div className="cr2-stars" aria-label={`Rating ${it.rating} out of 5`} style={starStyle}>
                    {Array.from({ length: 5 }).map((_, s) => (
                      <span
                        key={s}
                        className={`cr2-star ${s < (it.rating || 0) ? "on" : ""}`}
                        aria-hidden="true"
                      >
                        ★
                      </span>
                    ))}
                  </div>

                  <div className="cr2-nameRow">
                    <div className="cr2-name" style={nameStyle}>{it.name}</div>
                    {it.designation ? (
                      <div className="cr2-desig" style={roleStyle}>{it.designation}</div>
                    ) : null}
                  </div>
                </div>

                {/* if text got clamped, user taps “Read more” */}
              
              </article>
            ))}
          </div>

          {/* Optional small progress bar (no clicking) */}
         
        </div>
      </div>

    </section>
  );
}
